import {MainT} from '@/utils/i18n/main';
import {
  matchPath,
  Outlet,
  ScrollRestoration,
  useLocation,
} from 'react-router-dom';
import NavigationLoader from '../NavigationLoader';
import {useEffect, useRef} from 'react';
import {ROUTE} from '@/constants/main/route';
import {sendWebUvLog} from '@/utils/main';
import ChannelLeftMenuWrapper from '@/components/channel/pc/layout/LeftMenu/ChannelLeftMenuWrapper';
import {isChannelPath} from '@/utils/channel';
import {lowerStylePriority, SoopLayerProvider} from '@soop-ui/soop-ui';
import {
  SoopUiDialog,
  SoopUiShare,
  SoopUiSharePopup,
  SoopUiToast,
} from '@/components/ui';
import {BodyStyler, TopButton} from '@/components/common';
import SoopiComponent from '@/components/soopi/SoopiComponent';
import globalNavigation from '@/utils/global-navigation';
import useGlobalHelpRedirect from '@/hooks/help/useGlobalHelpRedirect';

const getIsPathValid = (pathname, routes) => {
  for (const key in routes) {
    const value = routes[key];
    if (typeof value === 'string') {
      if (matchPath(value, pathname)) {
        return true;
      }
    } else if (typeof value === 'object' && value !== null) {
      if (getIsPathValid(pathname, value)) {
        return true;
      }
    }
  }
  // 2차 로그인을 통해 들어올때 탐색쪽 url 구조가 location 의 search 로 파라미터를 받는 구조가 아니라서 토크/캠방 , 먹방/음식 이런류의 카테고리로 진입하면 서브도메인으로 인식하여 기존 라우터 규칙에 맞지않아 에러페이지로 이동해버린다.
  if (pathname.includes(ROUTE.DIRECTORY.CATEGORY.INDEX)) {
    const regex = /^\/directory\/category\/([^/]+\/[^/]+)\/(live|vod|catch)$/;
    const match = pathname.match(regex);
    if (match) {
      return true;
    }
  }
  return false;
};

const DEFAULT_NAV = {
  showHamburgerMenu: true,
  showSearchBar: true,
  showSNB: true,
  logoSecondary: {text: '', href: ''},
  showFooterCopyrightOnly: false,
};

const NAV_RULES = [
  {
    pattern: '/help/*',
    options: {
      showHamburgerMenu: false,
      showSearchBar: false,
      showSNB: false,
      logoSecondary: {text: MainT('고객센터'), href: '/help'},
    },
  },
  {
    pattern: '/station/*',
    options: {
      showSNB: false,
      showFooterCopyrightOnly: true,
    },
  },
];

const resolveNavOptions = (pathname) => {
  let merged = {...DEFAULT_NAV};
  for (const rule of NAV_RULES) {
    if (matchPath({path: rule.pattern, end: false}, pathname)) {
      return {...merged, ...rule.options};
    }
  }
  return merged;
};

const applyNavDiff = (prev, next) => {
  const changed = (key) =>
    !prev || JSON.stringify(prev[key]) !== JSON.stringify(next[key]);

  if (changed('showHamburgerMenu')) {
    globalNavigation.setShowHamburgerMenu(next.showHamburgerMenu);
  }
  if (changed('showSearchBar')) {
    globalNavigation.setShowSearchBar(next.showSearchBar);
  }
  if (changed('showSNB')) {
    globalNavigation.setLayoutVisibility('SNB', next.showSNB);
  }
  if (changed('logoSecondary')) {
    const logo = next.logoSecondary || {text: '', href: ''};
    globalNavigation.setLogoSecondary(logo.text, logo.href);
  }
  if (changed('showFooterCopyrightOnly')) {
    globalNavigation.setFooterCopyrightOnly(next.showFooterCopyrightOnly);
  }
};

const LowerStylePriority = () => {
  const location = useLocation();
  useEffect(() => {
    lowerStylePriority();
  }, [location.pathname]);
  return null;
};

const useLocationChangedHandler = () => {
  const location = useLocation();
  const prevLocationRef = useRef('');
  const isChannel = isChannelPath(location.pathname);
  const prevNavRef = useRef(null);

  useEffect(() => {
    // 이전 location과 현재 location의 경로+쿼리스트링이 같다면 아무것도 하지 않음
    if (
      prevLocationRef.current.pathname === location.pathname &&
      prevLocationRef.current.search === location.search
    ) {
      return;
    }
    // 아래부터는 location이 실제로 변경되었을 때만 실행

    let prevPathName = prevLocationRef.current.pathname ?? '';
    let prevSearchQuery =
      typeof prevLocationRef.current.search === 'string'
        ? prevLocationRef.current.search
        : ''; // location 없을때 search 에서 function 타입으로 나와 function 일 경우 빈값으로 대체

    // webuv 전송
    const prevUrlForLog = `${window.location.origin}${prevPathName}${prevSearchQuery}`;
    sendWebUvLog(prevUrlForLog);

    const nextNav = resolveNavOptions(location.pathname);
    applyNavDiff(prevNavRef.current, nextNav);
    prevNavRef.current = nextNav;

    prevLocationRef.current = location;
  }, [location]);
  return {isChannel};
};

const Layout = () => {
  const {isChannel} = useLocationChangedHandler();
  const location = useLocation();
  const isPathValid = getIsPathValid(location.pathname, ROUTE);
  const shouldRedirectToGlobalHelp = useGlobalHelpRedirect();

  // 해외 고객센터로 리다이렉트 중이면 GNB 렌더링 전에 리턴
  if (shouldRedirectToGlobalHelp) {
    return null;
  }

  return (
    <>
      <ScrollRestoration />
      {isPathValid && (
        <SoopLayerProvider>
          <div
            id='soop_wrap'
            className={`soop-wrap-${location.pathname.split('/')[1]}`}>
            <div id='soop-gnb'></div>

            {/* 방송국 페이지인 경우에만 전용 LNB 렌더링 */}
            {isChannel && <ChannelLeftMenuWrapper />}

            <div id='soop-snb'></div>
            <main id='main'>
              <Outlet />
              <div id='soop-fnb'></div>
            </main>
            <div id='soopi_wrap'></div>
          </div>

          <NavigationLoader />
        </SoopLayerProvider>
      )}
      {!isPathValid && <Outlet />}
      <LowerStylePriority />
      <SoopUiDialog />
      <SoopUiToast />
      <SoopUiShare />
      <SoopUiSharePopup />
      <BodyStyler />
      <TopButton />
      <SoopiComponent />
    </>
  );
};

export default Layout;
